/**
 * LoginPage für takeoff.bidi.pro (2026-05-05).
 *
 * Single-User-Login mit Username + Passwort. Atelier-Stil mit hellem
 * Papier-Hintergrund + ember-Akzent (analog Studio-Dashboard). Keine
 * Selbst-Registrierung, kein Passwort-Reset, kein SSO-Pfad — bewusst
 * minimalistisch für genau einen User.
 *
 * Nach erfolgreichem Login wird auf den `next`-Query-Param oder `/`
 * weitergeleitet.
 */

import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";

export default function LoginPage() {
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bereits eingeloggt? Direkt weiterleiten.
  useEffect(() => {
    if (state.status === "authenticated") {
      const params = new URLSearchParams(location.search);
      const next = params.get("next");
      navigate(next && next.startsWith("/") ? next : "/", { replace: true });
    }
  }, [state, location.search, navigate]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await login(username.trim(), password);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    const params = new URLSearchParams(location.search);
    const next = params.get("next");
    navigate(next && next.startsWith("/") ? next : "/", { replace: true });
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: "var(--bd-bg)",
        backgroundImage:
          "radial-gradient(circle at 30% 20%, color-mix(in oklab, var(--bd-ember) 6%, transparent) 0%, transparent 50%), radial-gradient(circle at 70% 80%, color-mix(in oklab, var(--bd-ember) 4%, transparent) 0%, transparent 50%)",
      }}
    >
      <div
        className="w-full max-w-sm rounded-xl p-8"
        style={{
          background: "var(--bd-surface)",
          border: "1px solid var(--bd-line)",
          boxShadow:
            "0 1px 0 rgba(255,255,255,0.4) inset, 0 12px 32px rgba(0,0,0,0.08)",
        }}
      >
        {/* Logo + Titel */}
        <div className="flex items-center gap-2.5 mb-6">
          <span
            className="flex items-center justify-center text-white font-bold"
            style={{
              width: 32,
              height: 32,
              borderRadius: 7,
              background: "var(--bd-ember)",
              fontSize: 16,
              boxShadow:
                "0 1px 0 rgba(255,255,255,0.4) inset, 0 4px 8px color-mix(in oklab, var(--bd-ember) 35%, transparent)",
            }}
          >
            b
          </span>
          <div>
            <div
              className="font-semibold"
              style={{ fontSize: 16, color: "var(--bd-text)", letterSpacing: -0.2 }}
            >
              bidi · takeoff
            </div>
            <div
              style={{
                fontSize: 10,
                color: "var(--bd-text-3)",
                letterSpacing: 0.4,
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              Atelier
            </div>
          </div>
        </div>

        <h1
          className="font-semibold mb-1"
          style={{
            fontSize: 18,
            color: "var(--bd-text)",
            letterSpacing: -0.2,
          }}
        >
          Anmelden
        </h1>
        <p
          className="mb-6"
          style={{ fontSize: 12, color: "var(--bd-text-3)", lineHeight: 1.5 }}
        >
          KI-Mengenermittlung aus 2D-Bauplänen für DACH-Markt.
        </p>

        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label
              className="block mb-1.5 font-medium"
              style={{ fontSize: 11, color: "var(--bd-text-2)" }}
            >
              Benutzername
            </label>
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 rounded outline-none"
              style={{
                fontSize: 13,
                background: "var(--bd-bg)",
                border: "1px solid var(--bd-line)",
                color: "var(--bd-text)",
              }}
              autoFocus
              required
            />
          </div>
          <div>
            <label
              className="block mb-1.5 font-medium"
              style={{ fontSize: 11, color: "var(--bd-text-2)" }}
            >
              Passwort
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 rounded outline-none"
              style={{
                fontSize: 13,
                background: "var(--bd-bg)",
                border: "1px solid var(--bd-line)",
                color: "var(--bd-text)",
              }}
              required
            />
          </div>

          {error && (
            <div
              className="rounded px-3 py-2"
              style={{
                fontSize: 12,
                background: "color-mix(in oklab, #dc2626 8%, transparent)",
                border: "1px solid color-mix(in oklab, #dc2626 30%, transparent)",
                color: "#991b1b",
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !username || !password}
            className="w-full py-2 rounded font-semibold text-white transition-opacity"
            style={{
              fontSize: 13,
              background: "var(--bd-ember)",
              opacity: submitting || !username || !password ? 0.6 : 1,
              cursor:
                submitting || !username || !password ? "not-allowed" : "pointer",
              boxShadow:
                "0 1px 0 rgba(255,255,255,0.4) inset, 0 4px 8px color-mix(in oklab, var(--bd-ember) 35%, transparent)",
            }}
          >
            {submitting ? "Wird angemeldet …" : "Anmelden"}
          </button>
        </form>

        <div
          className="mt-6 pt-4 border-t text-center"
          style={{ borderColor: "var(--bd-line)" }}
        >
          <span style={{ fontSize: 10, color: "var(--bd-text-4)" }}>
            takeoff.bidi.pro · Single-User-Zugang
          </span>
        </div>
      </div>
    </div>
  );
}
